import React, { useState, useEffect } from "react";
import { C, fmt, shadow, text } from "../theme";
import { api } from "../api";
import { usePermissions } from "../PermissionContext";
import { Card, MetricCard, StatusPill, DataTable } from "../components/ui";
import { aorBudgetChip } from "../lib/budgetChip";

// A clickable workqueue card: what needs this role's action and where to act.
function WorkqueueCard({ label, count, hint, accent, onClick }) {
  const active = count > 0;
  return (
    <button onClick={onClick} style={{
      flex: 1, minWidth: 190, textAlign: "left", cursor: "pointer", fontFamily: "inherit",
      background: C.white,
      border: `1px solid ${active ? accent : C.gray200}`,
      borderLeft: `4px solid ${active ? accent : C.gray200}`,
      borderRadius: 10, boxShadow: shadow, padding: "18px 20px",
    }}>
      <div style={{ fontSize: text.sm, color: C.gray600, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: text.metric, fontWeight: 600, letterSpacing: "-0.02em", lineHeight: 1.1, color: active ? accent : C.gray1100 }}>
        {count}
      </div>
      <div style={{ fontSize: text.sm, color: C.gray500, marginTop: 6 }}>{hint}</div>
    </button>
  );
}

export default function Dashboard({ onNavigate, onOpenDoc }) {
  const { me, can } = usePermissions();
  // The dashboard is role-aware (UX-15/UX-44): it fetches and shows only the
  // queues the signed-in role can act on, instead of AOR metrics for everyone.
  const canAor = can("aor", "view");
  const canPayment = can("payment", "view");
  const canApprovalsQueue = canAor || canPayment; // mirrors /api/approvals/pending's gate
  const canInvoice = can("invoice", "view");
  const canEval = can("vendorEvaluation", "evaluate");

  const [aors, setAors] = useState([]);
  const [pending, setPending] = useState([]);
  const [batches, setBatches] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [evals, setEvals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const safe = (p) => p.then((d) => (Array.isArray(d) ? d : [])).catch(() => []);
    Promise.all([
      canAor ? safe(api.getAORs()) : Promise.resolve([]),
      canApprovalsQueue ? safe(api.getPending()) : Promise.resolve([]),
      canPayment ? safe(api.getPaymentBatches()) : Promise.resolve([]),
      canInvoice ? safe(fetch("/api/invoices").then((r) => (r.ok ? r.json() : []))) : Promise.resolve([]),
      canEval ? safe(api.getPendingEvaluations()) : Promise.resolve([]),
    ]).then(([a, p, b, inv, ev]) => {
      setAors(a); setPending(p); setBatches(b); setInvoices(inv); setEvals(ev);
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: C.gray500 }}>Loading...</div>;

  const totalAORValue = aors.reduce((s, p) => s + p.total, 0);
  const approvedAORs = aors.filter((p) => p.status === "APPROVED").length;
  const returnedAORs = aors.filter((p) => p.status === "RETURNED" && me && p.requestorId === me.id);
  const returnedInvoices = invoices.filter((i) => i.status === "RETURNED" && me && i.submittedById === me.id);
  const draftBatches = batches.filter((b) => b.status === "DRAFT");
  const approvedBatches = batches.filter((b) => b.status === "APPROVED");

  // One card per queue this role owns; each navigates to the page that acts on it.
  const queue = [];
  if (canApprovalsQueue) queue.push({ label: "Pending your approval", count: pending.length, page: "approvals", hint: "Documents waiting for your decision", accent: C.warning });
  if (canAor && returnedAORs.length > 0) queue.push({ label: "Returned to you", count: returnedAORs.length, page: "aor", hint: "AORs an approver sent back — amend and resubmit", accent: C.danger });
  if (canInvoice && returnedInvoices.length > 0) queue.push({ label: "Returned invoices", count: returnedInvoices.length, page: "invoice", hint: "Invoices an approver sent back — amend and resubmit", accent: C.danger });
  if (can("payment", "prepare")) queue.push({ label: "Draft payment runs", count: draftBatches.length, page: "payments", hint: "Prepared but not yet submitted for approval", accent: C.info });
  if (can("payment", "execute")) queue.push({ label: "Awaiting execution", count: approvedBatches.length, page: "payments", hint: "Approved payment runs ready to execute", accent: C.success });
  if (canEval) queue.push({ label: "Pending evaluations", count: evals.length, page: "tender-evals", hint: "Tender bids waiting for your scores", accent: C.purple });

  const metrics = [
    { label: "Total AORs", value: aors.length },
    { label: "Approved", value: approvedAORs, accent: C.success },
    { label: "Total AOR Value", value: fmt(totalAORValue), accent: C.primary },
  ];

  const columns = [
    { key: "aorNumber", label: "AOR No.", render: (aor) => <span style={{ fontWeight: 600, color: C.primary }}>{aor.aorNumber}</span> },
    { key: "description", label: "Description" },
    { key: "businessUnit", label: "Business Unit" },
    { key: "budgetRef", label: "Budget Ref", render: (aor) => <span style={{ fontSize: text.xs, color: C.primary }}>{aor.budgetRef}</span> },
    { key: "total", label: "Total", render: (aor) => fmt(aor.total) },
    { key: "budget", label: "Budget", render: (aor) => { const chip = aorBudgetChip(aor); return <span title={chip.title}><StatusPill status={chip.status} /></span>; } },
    { key: "status", label: "Status", render: (aor) => <StatusPill status={aor.status} /> },
    {
      key: "open", label: "", render: (aor) => (
        <button onClick={() => onOpenDoc && onOpenDoc("aor", aor.id)} style={{
          background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit",
          fontSize: text.sm, fontWeight: 600, color: C.primary, textDecoration: "underline",
        }}>Open</button>
      ),
    },
  ];

  return (
    <div>
      <h1 style={{ fontSize: text.h1, fontWeight: 600, color: C.gray1100, margin: "0 0 4px" }}>Dashboard</h1>
      <p style={{ fontSize: text.md, color: C.gray600, margin: "0 0 24px" }}>Procure-to-Pay overview</p>

      {queue.length > 0 && (
        <>
          <div style={{ fontSize: text.lg, fontWeight: 700, color: C.gray1100, marginBottom: 10 }}>Your work queue</div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 24 }}>
            {queue.map((q) => (
              <WorkqueueCard key={q.label} label={q.label} count={q.count} hint={q.hint} accent={q.accent} onClick={() => onNavigate(q.page)} />
            ))}
          </div>
        </>
      )}

      {canAor && (
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 24 }}>
          {metrics.map((m, i) => (
            <MetricCard key={i} label={m.label} value={m.value} accent={m.accent} />
          ))}
        </div>
      )}

      {pending.length > 0 && (
        <Card style={{ marginBottom: 24 }}>
          <div style={{ fontSize: text.lg, fontWeight: 700, color: C.warning, marginBottom: 12 }}>⚠ Pending Your Action</div>
          {pending.map((a) => (
            <div key={a.id} style={{ display: "flex", gap: 12, alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${C.gray100}` }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: text.md, fontWeight: 700 }}>{a.docType}: {a.aor?.aorNumber || a.po?.poNumber || a.invoice?.invoiceNumber || a.virement?.virNumber || a.paymentBatch?.batchNumber}</div>
                <div style={{ fontSize: text.sm, color: C.gray600 }}>Layer {a.layer} • {a.approverRole}</div>
              </div>
              <StatusPill status="PENDING_ACTION" />
              <button onClick={() => onNavigate("approvals")} style={{ background: C.primary, color: C.white, border: "none", borderRadius: 6, padding: "6px 14px", fontSize: text.sm, fontWeight: 600, cursor: "pointer" }}>Review</button>
            </div>
          ))}
        </Card>
      )}

      {canAor && (
        <Card>
          <div style={{ fontSize: text.lg, fontWeight: 700, marginBottom: 12, color: C.gray1100 }}>Recent AORs</div>
          <DataTable columns={columns} rows={aors} rowKey={(aor) => aor.id} />
        </Card>
      )}
    </div>
  );
}
